import { AlertTriangle, ServerCog } from 'lucide-react';
import { Explain } from '@/components/Explain';
import { CopyId } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { PlayerInspectionResult, PlayerIpRow, SessionRow } from '@/types';

/**
 * Sessions & devices — the multi-accounting surface.
 *
 * ⚠️ The IP table is split in two on purpose. `public` addresses can genuinely
 * link accounts. `proxy/local` ones (10.x, 127.x, CGNAT…) are almost always the
 * game's nginx proxy, NOT the player's device — so they are greyed, labelled
 * unreliable, and (server-side) never raise the risk score. Presenting them as
 * "5 accounts on one IP" would smear innocent players.
 */
export function SessionsDevices({
  result,
}: {
  result: PlayerInspectionResult;
}) {
  const { sessions, ipLinks } = result;
  const demo = sessions.filter((s) => s.is_demo).length;
  const locales = uniq(sessions.map((s) => s.locale).filter(Boolean) as string[]);

  return (
    <section className="border-b border-hairline">
      <div className="grid gap-px bg-hairline md:grid-cols-3">
        <Stat label="session records" value={String(sessions.length)} sub={`${demo} demo · ${sessions.length - demo} real`} />
        <Stat label="locales" value={String(locales.length)} sub={locales.slice(0, 4).join(', ') || '—'} />
        <Stat
          label={<Explain term="shared_ip">public IPs</Explain>}
          value={String(ipLinks.public.length)}
          sub={`${ipLinks.public.filter((i) => i.others > 0).length} shared with others`}
        />
      </div>

      {/* Public IPs — the ones that can actually link accounts. */}
      <IpTable
        title="Public IPs"
        note="A routable address. Accounts sharing one may be the same person."
        rows={ipLinks.public}
        emphasiseShared
      />

      {/* Proxy / local — shown for completeness, never trusted. */}
      {ipLinks.proxy.length > 0 && (
        <IpTable
          title="Proxy / local IPs"
          note="Private or reserved addresses — usually the game's own proxy, not the player's device. Not a reliable link, and never counted toward risk."
          rows={ipLinks.proxy}
          muted
        />
      )}

      <SessionList sessions={sessions} />
    </section>
  );
}

function IpTable({
  title,
  note,
  rows,
  emphasiseShared,
  muted,
}: {
  title: string;
  note: string;
  rows: PlayerIpRow[];
  emphasiseShared?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="border-t border-hairline">
      <div className="flex items-start gap-2 px-4 py-2">
        {muted ? (
          <ServerCog size={13} className="mt-[2px] shrink-0 text-text-faint" />
        ) : (
          <AlertTriangle size={13} className={cn('mt-[2px] shrink-0', 'text-text-faint')} />
        )}
        <div>
          <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
            {title}
          </span>
          <span className="tnum ml-2 font-mono text-[11px] text-text-faint">{rows.length}</span>
          <p className="mt-0.5 max-w-[560px] text-[11.5px] leading-relaxed text-text-dim">{note}</p>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className={cn('w-full border-collapse text-[12px]', muted && 'opacity-70')}>
            <thead>
              <tr className="border-y border-hairline bg-surface-sunken">
                <Th align="left">IP</Th>
                <Th align="right">bets</Th>
                <Th align="right">other accounts</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((ip) => {
                const shared = ip.others > 0;
                return (
                  <tr key={ip.ip} className="border-b border-hairline/50 last:border-0">
                    <td className="px-3 py-1.5">
                      <CopyId value={ip.ip} className={muted ? 'text-text-faint' : 'text-text'} />
                    </td>
                    <td className="tnum px-3 py-1.5 text-right font-mono text-text-dim">{ip.hits}</td>
                    <td className="tnum px-3 py-1.5 text-right font-mono">
                      {shared ? (
                        <span
                          className={cn(
                            emphasiseShared && ip.others >= 5
                              ? 'text-crit'
                              : emphasiseShared && ip.others >= 2
                                ? 'text-warn'
                                : 'text-text-dim',
                          )}
                        >
                          {ip.others}
                        </span>
                      ) : (
                        <span className="text-text-faint">0</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SessionList({ sessions }: { sessions: SessionRow[] }) {
  if (sessions.length === 0) return null;
  const shown = sessions.slice(0, 12);
  return (
    <div className="border-t border-hairline">
      <div className="px-4 py-2 font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
        Recent sessions
        <span className="tnum ml-2 font-mono text-[11px] text-text-faint">
          {shown.length} of {sessions.length}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="border-y border-hairline bg-surface-sunken">
              <Th align="left">session</Th>
              <Th align="left">currency</Th>
              <Th align="left">locale</Th>
              <Th align="left">demo</Th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} className="border-b border-hairline/50 last:border-0">
                <td className="px-3 py-1.5">
                  <CopyId value={s.session_id ?? String(s.id)} truncate />
                </td>
                <td className="px-3 py-1.5 font-mono text-[11.5px] text-text-dim">{s.currency ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-[11.5px] text-text-dim">{s.locale ?? '—'}</td>
                <td className="px-3 py-1.5">
                  {s.is_demo ? (
                    <span className="border border-warn/40 px-1 font-condensed text-[9px] uppercase tracking-wider text-warn">
                      demo
                    </span>
                  ) : (
                    <span className="text-text-faint">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: React.ReactNode; value: string; sub?: string }) {
  return (
    <div className="bg-surface px-4 py-2.5">
      <div className="eyebrow mb-1">{label}</div>
      <div className="tnum font-mono text-[17px] font-semibold text-text">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-text-dim">{sub}</div>}
    </div>
  );
}

function Th({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={cn(
        'px-3 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-faint',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function uniq<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}
